import { TreePine, ChefHat, Car, Wifi, Croissant } from "lucide-react";
import { useLang } from "@/i18n";
import { PHOTOS } from "@/photos";
import { FadeUp } from "./Reveal";

function PhotoTile({ src, alt, icon: Icon, title, desc, className = "", dark = false }) {
  return (
    <div className={`group relative overflow-hidden rounded-3xl ${className}`}>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
      />
      <div className={`absolute inset-0 ${dark ? "bg-gradient-to-t from-stone-950/85 via-stone-950/25 to-transparent" : "bg-gradient-to-t from-teal-950/80 via-teal-950/20 to-transparent"}`} />
      <div className="relative flex h-full flex-col justify-end gap-1 p-6 sm:p-8">
        <span className="mb-2 flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 text-pink-300 backdrop-blur-md">
          <Icon className="h-5 w-5" />
        </span>
        <h3 className="font-display text-xl font-bold text-white sm:text-2xl">{title}</h3>
        <p className="text-sm text-white/75">{desc}</p>
      </div>
    </div>
  );
}

export default function Amenities() {
  const { t } = useLang();
  const a = t.amenities;

  return (
    <section id="servizi" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
      <FadeUp>
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-teal-700">{a.eyebrow}</p>
        <h2 className="font-display mt-3 max-w-2xl text-3xl font-bold tracking-tight text-stone-900 sm:text-4xl lg:text-5xl">
          {a.title}
        </h2>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-stone-500 sm:text-lg">{a.subtitle}</p>
      </FadeUp>

      <FadeUp delay={0.15} className="mt-14">
        <div className="grid grid-cols-12 gap-4 md:auto-rows-[230px]" data-testid="amenities-bento-grid">
          <PhotoTile
            src={PHOTOS.hero}
            alt={a.garden.title}
            icon={TreePine}
            title={a.garden.title}
            desc={a.garden.desc}
            dark
            className="col-span-12 min-h-[280px] md:col-span-8 md:row-span-2 md:min-h-0"
          />
          <PhotoTile
            src={PHOTOS.kitchen}
            alt={a.kitchen.title}
            icon={ChefHat}
            title={a.kitchen.title}
            desc={a.kitchen.desc}
            className="col-span-12 min-h-[220px] md:col-span-4 md:min-h-0"
          />
          <div className="col-span-12 flex min-h-[200px] flex-col justify-between rounded-3xl bg-gradient-to-br from-pink-600 to-pink-800 p-6 sm:p-8 md:col-span-4 md:min-h-0">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 text-pink-100">
              <Car className="h-5 w-5" />
            </span>
            <div>
              <h3 className="font-display text-xl font-bold text-white sm:text-2xl">{a.parking.title}</h3>
              <p className="mt-1 text-sm text-pink-100/80">{a.parking.desc}</p>
            </div>
          </div>
          <div className="col-span-12 flex min-h-[200px] flex-col justify-between rounded-3xl bg-teal-700 p-6 sm:p-8 md:col-span-4 md:min-h-0">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 text-teal-100">
              <Wifi className="h-5 w-5" />
            </span>
            <div>
              <h3 className="font-display text-xl font-bold text-white sm:text-2xl">{a.wifi.title}</h3>
              <p className="mt-1 text-sm text-teal-100/80">{a.wifi.desc}</p>
            </div>
          </div>
          <PhotoTile
            src={PHOTOS.breakfast}
            alt={a.breakfastA.title}
            icon={Croissant}
            title={a.breakfastA.title}
            desc={a.breakfastA.desc}
            dark
            className="col-span-12 min-h-[220px] md:col-span-8 md:min-h-0"
          />
        </div>
      </FadeUp>
    </section>
  );
}
